/* ==========================================================================
   Lenora Fine — header behaviour
   1. Scroll: transparent at top (homepage) → hides on scroll down → slides back
      in, solid, on scroll up.
   2. Slide-out nav panel from the left (mobile menu).
   2b. Desktop hover dropdowns: mega menu + small dropdown.
   3. Accordions inside the panel (e.g. Shop by Category).
   4. Centred search bar.
   ========================================================================== */
(function () {
  'use strict';

  const header = document.querySelector('[data-site-header]');
  if (!header) return;

  const drawer = document.querySelector('[data-nav-drawer]');
  const drawerPanel = drawer && drawer.querySelector('.nav-drawer__panel');
  const searchPanel = header.querySelector('[data-search-panel]');
  const searchToggle = header.querySelector('[data-search-toggle]');
  const isTransparentPage = header.dataset.transparent === 'true';

  let lastFocused = null;

  /* ---------------------------------------------------------------------- */
  /* 1. Scroll behaviour                                                     */
  /* ---------------------------------------------------------------------- */
  const TOP_THRESHOLD = 10; // px from top that counts as "at the top"
  const DELTA = 6;          // ignore tiny scroll jitters
  let lastY = window.scrollY;
  let ticking = false;

  function isLocked() {
    return document.body.classList.contains('is-locked') ||
      header.classList.contains('is-search-open') ||
      header.classList.contains('is-menu-open');
  }

  function onScroll() {
    const y = Math.max(window.scrollY, 0);
    const headerH = header.offsetHeight;

    if (y <= TOP_THRESHOLD) {
      header.classList.remove('is-hidden');
      if (isTransparentPage) header.classList.remove('is-solid');
    } else if (!isLocked() && Math.abs(y - lastY) > DELTA) {
      if (y > lastY && y > headerH) {
        header.classList.add('is-hidden');   // scrolling down
      } else if (y < lastY) {
        header.classList.remove('is-hidden'); // scrolling up
        header.classList.add('is-solid');
      }
    }

    if (Math.abs(y - lastY) > DELTA || y <= TOP_THRESHOLD) lastY = y;
    ticking = false;
  }

  window.addEventListener('scroll', function () {
    if (!ticking) {
      window.requestAnimationFrame(onScroll);
      ticking = true;
    }
  }, { passive: true });

  // Page loaded part-way down (e.g. back button) → start solid
  if (window.scrollY > TOP_THRESHOLD) header.classList.add('is-solid');

   /* 1b. Desktop: reveal the hidden header when the mouse reaches the top */
  const HOVER_ZONE = 70; // px from the top of the screen that triggers it
  let revealedByHover = false;

  if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    document.addEventListener('mousemove', function (e) {
      const y = window.scrollY;
      if (y <= TOP_THRESHOLD || isLocked()) return;

      if (e.clientY <= HOVER_ZONE && header.classList.contains('is-hidden')) {
        header.classList.remove('is-hidden');
        header.classList.add('is-solid');
        revealedByHover = true;
      } else if (revealedByHover && e.clientY > header.offsetHeight + 40 && !header.matches(':hover')) {
        header.classList.add('is-hidden');
        revealedByHover = false;
      }
    }, { passive: true });

    window.addEventListener('scroll', function () { revealedByHover = false; }, { passive: true });
  }

  /* ---------------------------------------------------------------------- */
  /* 2. Slide-out nav panel                                                  */
  /* ---------------------------------------------------------------------- */
  const openers = document.querySelectorAll('[data-drawer-open]');

  function openDrawer(viewName, trigger) {
    if (!drawer) return;
    closeSearch();

    drawer.querySelectorAll('[data-drawer-view]').forEach(function (view) {
      view.hidden = view.dataset.drawerView !== viewName;
    });

    openers.forEach(function (btn) {
      btn.setAttribute('aria-expanded', btn === trigger ? 'true' : 'false');
    });

    lastFocused = trigger || document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('is-locked');
    header.classList.remove('is-hidden');
    window.setTimeout(function () { drawerPanel.focus(); }, 50);
  }

  function closeDrawer() {
    if (!drawer || !drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('is-locked');
    openers.forEach(function (btn) { btn.setAttribute('aria-expanded', 'false'); });
    if (lastFocused) lastFocused.focus();
  }

  openers.forEach(function (btn) {
    btn.addEventListener('click', function () {
      const view = btn.dataset.drawerOpen;
      const alreadyOpen = drawer.classList.contains('is-open') &&
        btn.getAttribute('aria-expanded') === 'true';
      alreadyOpen ? closeDrawer() : openDrawer(view, btn);
    });
  });

  if (drawer) {
    drawer.querySelectorAll('[data-drawer-close]').forEach(function (el) {
      el.addEventListener('click', closeDrawer);
    });

    // Keep keyboard focus inside the open panel
    drawer.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      const focusable = Array.prototype.filter.call(
        drawerPanel.querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])'),
        function (el) { return el.offsetParent !== null; }
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === drawerPanel)) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    });
  }

  /* ---------------------------------------------------------------------- */
  /* 3. Accordions inside the panel                                          */
  /* ---------------------------------------------------------------------- */
  document.querySelectorAll('[data-nav-accordion]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      const sub = document.getElementById(btn.getAttribute('aria-controls'));
      const expanded = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!expanded));
      if (sub) sub.hidden = expanded;
    });
  });

  /* ---------------------------------------------------------------------- */
  /* 3b. Desktop hover dropdowns (mega menu + small dropdown)                */
  /* ---------------------------------------------------------------------- */
  const navItems = Array.prototype.slice.call(header.querySelectorAll('[data-nav-item]'));
  const desktop = window.matchMedia('(min-width: 990px)');
  const OPEN_DELAY = 120;
  const CLOSE_DELAY = 220;
  let openItem = null;
  let openTimer = null;
  let closeTimer = null;

  function setOpen(item) {
    if (openItem === item) return;
    if (openItem) {
      openItem.classList.remove('is-open');
      openItem.querySelector('[data-nav-trigger]').setAttribute('aria-expanded', 'false');
    }
    openItem = item;
    if (item) {
      closeSearch();
      item.classList.add('is-open');
      item.querySelector('[data-nav-trigger]').setAttribute('aria-expanded', 'true');
      header.classList.add('is-menu-open');
      header.classList.remove('is-hidden');
    } else {
      header.classList.remove('is-menu-open');
    }
  }

  function scheduleOpen(item) {
    clearTimeout(closeTimer);
    clearTimeout(openTimer);
    // Moving between headings while one is open switches straight away
    if (openItem) setOpen(item);
    else openTimer = setTimeout(function () { setOpen(item); }, OPEN_DELAY);
  }

  function scheduleClose() {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
    closeTimer = setTimeout(function () { setOpen(null); }, CLOSE_DELAY);
  }

  navItems.forEach(function (item) {
    const trigger = item.querySelector('[data-nav-trigger]');

    item.addEventListener('mouseenter', function () { if (desktop.matches) scheduleOpen(item); });
    item.addEventListener('mouseleave', function () { if (desktop.matches) scheduleClose(); });

    // Touch screens (e.g. iPad landscape): first tap opens, second tap follows the link
    trigger.addEventListener('pointerdown', function (e) { trigger._touch = e.pointerType === 'touch'; });
    trigger.addEventListener('click', function (e) {
      if (!desktop.matches) return;
      if (trigger._touch && openItem !== item) { e.preventDefault(); setOpen(item); }
    });

    // Keyboard: arrow down / space opens, focus moving out closes
    trigger.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown' || e.key === ' ') {
        e.preventDefault();
        setOpen(item);
        const first = item.querySelector('[data-nav-panel] a');
        if (first) first.focus();
      }
    });
    item.addEventListener('focusout', function (e) {
      if (!item.contains(e.relatedTarget) && openItem === item) setOpen(null);
    });
  });

  // Tap outside closes
  document.addEventListener('pointerdown', function (e) {
    if (openItem && !openItem.contains(e.target)) setOpen(null);
  });
  desktop.addEventListener('change', function () { setOpen(null); });

  /* ---------------------------------------------------------------------- */
  /* 4. Search bar                                                           */
  /* ---------------------------------------------------------------------- */
  function openSearch() {
    if (!searchPanel) return;
    searchPanel.hidden = false;
    header.classList.add('is-search-open');
    header.classList.remove('is-hidden');
    searchToggle.setAttribute('aria-expanded', 'true');
    const input = searchPanel.querySelector('input[type="search"]');
    if (input) input.focus();
  }

  function closeSearch() {
    if (!searchPanel || searchPanel.hidden) return;
    searchPanel.hidden = true;
    header.classList.remove('is-search-open');
    searchToggle.setAttribute('aria-expanded', 'false');
  }

  if (searchToggle) {
    searchToggle.addEventListener('click', function () {
      searchPanel.hidden ? openSearch() : (closeSearch(), searchToggle.focus());
    });
  }

  const searchClose = header.querySelector('[data-search-close]');
  if (searchClose) {
    searchClose.addEventListener('click', function () {
      closeSearch();
      searchToggle.focus();
    });
  }

  // Click outside the header closes search
  document.addEventListener('click', function (e) {
    if (searchPanel && !searchPanel.hidden && !header.contains(e.target)) closeSearch();
  });

  /* ---------------------------------------------------------------------- */
  /* Escape closes whatever is open                                          */
  /* ---------------------------------------------------------------------- */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (openItem) { const t = openItem.querySelector('[data-nav-trigger]'); setOpen(null); t.focus(); return; }
    if (drawer && drawer.classList.contains('is-open')) closeDrawer();
    else if (searchPanel && !searchPanel.hidden) { closeSearch(); searchToggle.focus(); }
  });
})();
